import React, { useState, useEffect, useMemo } from 'react';
import { Select } from 'antd';
import moment from 'moment';
import { IContent, ITranslationHistory } from 'engine/store/articles/types';
import { calculateDiff, DiffRenderer } from 'helpers/diff-utils';
import {
  HistoryContainer,
  ComparisonWrapper,
  VersionColumn,
  VersionHeader,
  VersionLabel,
  ContentSection,
  FieldWrapper,
  FieldLabel,
  FieldContent,
  EmptyState,
} from './styled';

interface TranslationsHistoryProps {
  currentTranslation: IContent;
  language: string;
  visible: boolean;
}

interface VersionOption {
  value: string;
  label: string;
  data: ITranslationHistory | IContent;
  isCurrent?: boolean;
}

export const TranslationsHistory: React.FC<TranslationsHistoryProps> = ({
  currentTranslation,
  language,
  visible,
}) => {
  const [leftVersionId, setLeftVersionId] = useState<string>('current');
  const [rightVersionId, setRightVersionId] = useState<string>('');

  const history = currentTranslation?.history || [];

  // Build version options in descending order (current first, then most recent to oldest)
  const versionOptions: VersionOption[] = useMemo(() => {
    const options: VersionOption[] = [
      {
        value: 'current',
        label: 'Current Version',
        data: currentTranslation,
        isCurrent: true,
      },
    ];

    // Reverse history array to show most recent first (backend stores oldest first)
    const reversedHistory = [...history].reverse();
    
    reversedHistory.forEach((historyEntry, index) => {
      const date = historyEntry.date
        ? moment(historyEntry.date).format('MMM DD, YYYY HH:mm')
        : 'Unknown Date';
      
      // Construct user name from firstname and lastname
      const user = historyEntry.user as any;
      
      // Debug: Check what user data we're receiving
      console.log('History user data:', user);
      
      let userName = 'Unknown User';
      if (user) {
        if (user.firstname && user.lastname) {
          userName = `${user.firstname} ${user.lastname}`;
        } else if (user.firstname) {
          userName = user.firstname;
        } else if (user.lastname) {
          userName = user.lastname;
        } else if (user.name) {
          userName = user.name;
        } else if (user.email) {
          userName = user.email;
        }
      }
      
      const label = `${date} - ${userName}`;

      // Use the original index from the history array (before reversing)
      const originalIndex = history.length - 1 - index;
      
      options.push({
        value: `history-${originalIndex}`,
        label,
        data: historyEntry,
      });
    });

    return options;
  }, [currentTranslation, history]);

  // Set default right version (most recent history entry - last item in array)
  useEffect(() => {
    if (visible && history.length > 0 && !rightVersionId) {
      // Most recent is at the end of the array (history.length - 1)
      setRightVersionId(`history-${history.length - 1}`);
    }
  }, [visible, history, rightVersionId]);

  const getVersionData = (versionId: string): ITranslationHistory | IContent | null => {
    const option = versionOptions.find((opt) => opt.value === versionId);
    return option ? option.data : null;
  };

  // Filter options to prevent selecting the same version in both dropdowns
  const leftVersionOptions = versionOptions.filter((opt) => opt.value !== rightVersionId);
  const rightVersionOptions = versionOptions.filter((opt) => opt.value !== leftVersionId);

  const leftVersion = getVersionData(leftVersionId);
  const rightVersion = getVersionData(rightVersionId);

  // Calculate diffs
  const titleDiff = useMemo(() => {
    if (!leftVersion || !rightVersion) return [];
    return calculateDiff(rightVersion.title || '', leftVersion.title || '');
  }, [leftVersion, rightVersion]);

  const bodyDiff = useMemo(() => {
    if (!leftVersion || !rightVersion) return [];
    return calculateDiff(rightVersion.body || '', leftVersion.body || '');
  }, [leftVersion, rightVersion]);

  if (!visible) return null;

  if (history.length === 0) {
    return (
      <HistoryContainer>
        <EmptyState>No translation history available</EmptyState>
      </HistoryContainer>
    );
  }

  return (
    <HistoryContainer>
      <ComparisonWrapper>
        {/* Left Column */}
        <VersionColumn>
          <VersionHeader>
            <VersionLabel>Version A</VersionLabel>
            <Select
              value={leftVersionId}
              onChange={setLeftVersionId}
              style={{ width: '100%' }}
              options={leftVersionOptions}
            />
          </VersionHeader>
          <ContentSection>
            <FieldWrapper>
              <FieldLabel>Title</FieldLabel>
              <FieldContent>
                {leftVersion?.title || <span style={{ color: '#999' }}>No title</span>}
              </FieldContent>
            </FieldWrapper>
            <FieldWrapper>
              <FieldLabel>Body</FieldLabel>
              <FieldContent style={{ minHeight: '120px' }}>
                {leftVersion?.body || <span style={{ color: '#999' }}>No body</span>}
              </FieldContent>
            </FieldWrapper>
          </ContentSection>
        </VersionColumn>

        {/* Right Column */}
        <VersionColumn>
          <VersionHeader>
            <VersionLabel>Version B (Comparison)</VersionLabel>
            <Select
              value={rightVersionId}
              onChange={setRightVersionId}
              style={{ width: '100%' }}
              options={rightVersionOptions}
            />
          </VersionHeader>
          <ContentSection>
            <FieldWrapper>
              <FieldLabel>Title (Differences Highlighted)</FieldLabel>
              <FieldContent>
                {rightVersion && leftVersion ? (
                  <DiffRenderer diff={titleDiff} />
                ) : (
                  rightVersion?.title || <span style={{ color: '#999' }}>No title</span>
                )}
              </FieldContent>
            </FieldWrapper>
            <FieldWrapper>
              <FieldLabel>Body (Differences Highlighted)</FieldLabel>
              <FieldContent style={{ minHeight: '120px' }}>
                {rightVersion && leftVersion ? (
                  <DiffRenderer diff={bodyDiff} />
                ) : (
                  rightVersion?.body || <span style={{ color: '#999' }}>No body</span>
                )}
              </FieldContent>
            </FieldWrapper>
          </ContentSection>
        </VersionColumn>
      </ComparisonWrapper>
    </HistoryContainer>
  );
};

